<template>
    <tasks-store
        v-slot="{
            sid,
            is_loading: is_saving,
            data: task,
            error,
            store,
            forget
        }"
        @success="onTasksStoreSuccess"
    >
        <div class="bg-white rounded shadow">
            <base-form @submit="store(form)">
                <header class="flex items-center px-4 py-3 border-b sm:px-6 md:py-4">
                    <h1 class="text-lg font-normal">
                        Create a task
                    </h1>
                </header>

                <div class="px-4 py-3 border-b sm:px-6 md:py-6">
                    <validator-alert :sid="sid" />

                    <form-section>
                        <template slot="title">Basics</template>
                        <template slot="description">
                            What is the task?
                        </template>

                        <grid-layout>
                            <grid-layout-item>
                                <form-item
                                    :sid="sid"
                                    :is-required="true"
                                    name="title"
                                >
                                    <template slot="label">Title</template>

                                    <input-text
                                        v-model="form.title"
                                        :sid="sid"
                                        :disabled="is_saving"
                                        name="title"
                                    />
                                </form-item>
                            </grid-layout-item>

                            <grid-layout-item>
                                <form-item
                                    :sid="sid"
                                    name="description"
                                >
                                    <template slot="label">Description</template>

                                    <input-text
                                        v-model="form.description"
                                        :sid="sid"
                                        :disabled="is_saving"
                                        name="description"
                                    />
                                </form-item>
                            </grid-layout-item>
                        </grid-layout>
                    </form-section>
                </div>

                <div class="px-4 py-3 border-b sm:px-6 md:py-6">
                    <form-section>
                        <template slot="title">Context</template>
                        <template slot="description">
                            Is the task for a specific property, tenancy or other?
                        </template>

                        <grid-layout>
                            <template v-if="is_existing_taskable">
                                <grid-layout-item v-if="form.taskable.object === 'property'">
                                    <form-item
                                        :sid="sid"
                                        :is-required="true"
                                        name="taskable"
                                    >
                                        <template slot="label">Property</template>

                                        <input-property
                                            v-model="form.taskable"
                                            :sid="sid"
                                            :disabled="is_saving"
                                            :uuid="form.taskable.uuid"
                                            name="taskable"
                                        />

                                        <template slot="hint">
                                            <button
                                                @click.prevent="is_existing_taskable = false"
                                                class="text-blue"
                                            >
                                                Change context
                                            </button>
                                        </template>
                                    </form-item>
                                </grid-layout-item>

                                <grid-layout-item v-if="form.taskable.object === 'tenancy'">
                                    <form-item
                                        :sid="sid"
                                        :is-required="true"
                                        name="taskable"
                                    >
                                        <template slot="label">Tenancy</template>

                                        <input-tenancy
                                            v-model="form.taskable"
                                            :sid="sid"
                                            :disabled="is_saving"
                                            :uuid="form.taskable.uuid"
                                            name="taskable"
                                        />

                                        <template slot="hint">
                                            <button
                                                @click.prevent="is_existing_taskable = false"
                                                class="text-blue"
                                            >
                                                Change context
                                            </button>
                                        </template>
                                    </form-item>
                                </grid-layout-item>
                            </template>

                            <template v-else>
                                <grid-layout-item>
                                    <form-item
                                        :sid="sid"
                                        name="context"
                                    >
                                        <template slot="label">Is For</template>

                                        <input-select
                                            v-model="form.context"
                                            :sid="sid"
                                            :disabled="is_saving"
                                            placeholder="Select a context..."
                                            :options="[
                                                { value: '', label: 'No context' },
                                                { value: 'property', label: 'Property' },
                                                { value: 'tenancy', label: 'Tenancy' }
                                            ]"
                                            name="context"
                                        />
                                    </form-item>
                                </grid-layout-item>

                                <grid-layout-item v-if="form.context === 'property'">
                                    <form-item
                                        :sid="sid"
                                        :is-required="true"
                                        name="taskable"
                                    >
                                        <template slot="label">Property</template>

                                        <input-property
                                            v-model="form.taskable"
                                            :sid="sid"
                                            :disabled="is_saving"
                                            name="taskable"
                                        />
                                    </form-item>
                                </grid-layout-item>

                                <grid-layout-item v-if="form.context === 'tenancy'">
                                    <form-item
                                        :sid="sid"
                                        :is-required="true"
                                        name="taskable"
                                    >
                                        <template slot="label">Tenancy</template>

                                        <input-tenancy
                                            v-model="form.taskable"
                                            :sid="sid"
                                            :disabled="is_saving"
                                            name="taskable"
                                        />
                                    </form-item>
                                </grid-layout-item>
                            </template>
                        </grid-layout>
                    </form-section>
                </div>

                <div class="px-4 py-3 border-b sm:px-6 md:py-6">
                    <form-section>
                        <template slot="title">Reminder</template>
                        <template slot="description">
                            Would you like a reminder?
                        </template>

                        <grid-layout>
                            <grid-layout-item>
                                <form-item
                                    :sid="sid"
                                    name="remind_at"
                                >
                                    <template slot="label">Date</template>

                                    <input-date
                                        v-model="form.remind_at"
                                        :sid="sid"
                                        :disabled="is_saving"
                                        name="remind_at"
                                    />
                                </form-item>
                            </grid-layout-item>
                        </grid-layout>
                    </form-section>
                </div>

                <base-form-footer class="rounded-b">
                    <base-button
                        type="submit"
                        class="w-full btn-blue md:w-auto"
                    >
                        Create Task
                    </base-button>
                </base-form-footer>
            </base-form>

            <error-modal
                v-if="error && $error.is(error, { exclude: [422] })"
                :error="error"
            />

            <modal-success
                v-if="task"
                :is-open="show_created_modal"
                @close="(function () {
                    forget();
                    reset();
                })()"
            >
                <template slot="heading">Task created!</template>

                <p>
                    Your task has been created.
                </p>

                <div
                    slot="footer"
                    slot-scope="{ close }"
                    class="flex flex-col md:flex-row-reverse"
                >
                    <router-link
                        :to="{
                            name: $router.fix('dashboard.*.tasks.show'),
                            params: {
                                task: task.uuid
                            }
                        }"
                        class="text-center btn btn-green"
                    >
                        Go to Task
                    </router-link>

                    <button
                        type="button"
                        @click.prevent="close"
                        class="mt-3 text-center btn md:mt-0 md:mr-3"
                    >
                        Add another
                    </button>
                </div>
            </modal-success>
        </div>
    </tasks-store>
</template>

<script>
    import InputProperty from '../../../../components/input-property'
    import InputTenancy from '../../../../components/input-tenancy'
    import TasksStore from '../../../../components/tasks-store'

    export default {
        components: {
            InputProperty,
            InputTenancy,
            TasksStore
        },
        data () {
            return {
                is_existing_taskable: false,
                show_created_modal: false,

                form: {
                    title: '',
                    description: '',
                    context: null,
                    taskable: null,
                    remind_at: null
                }
            }
        },
        watch: {
            'form.context' () {
                this.form.taskable = null
            }
        },
        created () {
            if (this.$route.query.property) {
                this.form.context = 'property'
                this.form.taskable = {
                    object: 'property',
                    uuid: this.$route.query.property
                }
                this.is_existing_taskable = true
            } else if (this.$route.query.tenancy) {
                this.form.context = 'tenancy'
                this.form.taskable = {
                    object: 'tenancy',
                    uuid: this.$route.query.tenancy
                }
                this.is_existing_taskable = true
            }
        },
        methods: {
            reset () {
                this.$validator.reset(this.sid)

                Object.assign(this.$data, this.$options.data.apply(this))
            },
            onTasksStoreSuccess () {
                this.$track(
                    'dashboard._.tasks.create',
                    'form-submission',
                    'A user has created a task'
                )

                setTimeout(() => {
                    this.show_created_modal = true
                }, 0)
            }
        }
    }
</script>
